import React, { useEffect, useRef, useState } from "react";
// ArrowPathIcon : the retry action's icon. Every primary action on
// every sibling tool page declares an explicit variant and a
// Heroicon, and the retry button here previously relied on
// ActionButton's default with neither.
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import SEO from "../components/SEO";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import StepPanel from "../components/issuance/StepPanel";
import Step1Service from "../components/issuance/Step1Service";
import Step2Configure from "../components/issuance/Step2Configure";
import Step3Verify from "../components/issuance/Step3Verify";
import Step4Download from "../components/issuance/Step4Download";
import ActionButton from "../components/ActionButton";
// SESSION_LIFETIME_HOURS : interpolated into the restore-failure copy
// so the window a customer is told about can never drift from the value
// the session store actually enforces.
import {
  STEPS,
  KEY_TYPES,
  ACME_SERVERS,
  SESSION_LIFETIME_HOURS,
} from "../../shared/issuance/config";
import {
  apiGetSession,
  apiDiscardSession,
  ApiError,
  SHORT_CALL_TIMEOUT_MS,
  withTimeoutSignal,
} from "../issuance/apiClient";
import { saveState, loadState, clearState } from "../issuance/state";

/**
 * Step titles keyed by the STEPS constants, used by the sr-only
 * step-transition live region below. The strings match the StepPanel
 * `title` props verbatim so assistive technology hears the exact
 * heading the sighted customer sees. Kept as a module-level map (not
 * derived from the JSX) because the announcement must exist even
 * while a panel is collapsed to its completed summary row.
 */
const STEP_TITLES = {
  [STEPS.SERVICE]: "Select Your Service",
  [STEPS.CONFIGURE]: "Configure SSL Certificate",
  [STEPS.VERIFY]: "Verify Domain Ownership",
  [STEPS.DOWNLOAD]: "Download SSL Certificate",
};

/**
 * Tag a deliberate restore-schema throw as POSITIVE PROOF the saved
 * session cannot be restored (2026-08-14 go-public review) : the
 * transient-vs-gone fork in the restore effect wipes localStorage
 * ONLY for these tagged throws or a server 404. Without the tag, any
 * unexpected runtime throw in the restore mapping fell to the wipe
 * side by default and destroyed the only automatic pointer to a
 * still-live 48-hour session.
 */
function unrestorable(message) {
  const err = new Error(message);
  err.isUnrestorable = true;
  return err;
}

/**
 * Hosted SSL Certificate Issuance Tool - page root.
 *
 * Architecture : the customer's browser talks only to /api/issuance/*
 * on the Trustico® server. All cryptographic operations and
 * Automatic Certificate Management Environment (ACME) traffic
 * happen server-side ; the browser collects inputs, renders the
 * server-supplied challenge details, and downloads the issued
 * artifacts.
 *
 * Page state shape :
 *   step1Result   { serverId, label, serverUrl, loadedConfig? }
 *   step2Result   { sessionId, status, authorizations: [{ authUrl, domain, wildcard, status }],
 *                   domains, primaryDomain, keyType }
 *   (The issued bundle is NOT held at the page level : Step4Download
 *   owns it in its own state and stays mounted through the completed
 *   transition, so the page keeps no second copy of the SSL
 *   Certificate Private Key. PR #76 review blocking-bug fix,
 *   2026-07-03.)
 *
 * Persistence (one saved order per browser) : sessionId + step
 * indices are written to localStorage on every transition, under a
 * SINGLE fixed key - a second /issuance tab overwrites the first
 * tab's saved pointer, so the tool deliberately tracks ONE
 * in-progress order per browser. (2026-08-13 go-public cleanup :
 * this paragraph previously said "Cross-tab persistence", which
 * overclaimed - the storage never supported concurrent tabs, and
 * the Configuration File is the supported way to
 * hold several orders.) On restore the page calls
 * /api/issuance/sessions/:id to verify the session is still live on
 * the server ; if not, persistence is wiped and the customer starts
 * fresh.
 */
function Issuance() {
  const [currentStep, setCurrentStep] = useState(STEPS.SERVICE);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [step1Result, setStep1Result] = useState(null);
  const [step2Result, setStep2Result] = useState(null);
  // No step4Result state : Step4Download owns the issued bundle in its
  // own component state and stays mounted through the completed
  // transition (see the Step 4 render branch), so the page does not
  // need a second copy. The former page-level step4Result only gated a
  // static one-liner that the PR #76 review blocking-bug fix removed
  // (2026-07-03).

  const [restored, setRestored] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  // Transient restore failure (network error, rate limit, server
  // 5xx) : the saved pointer is KEPT and the customer gets a Retry
  // screen instead of the wipe (2026-08-14 go-public review,
  // "state-machine-error-handling" - the majority of restore failures
  // are recoverable, and wiping on them destroyed the only automatic
  // pointer to a still-live 48-hour session). Holds the customer-
  // facing message while restored stays false.
  const [restoreRetry, setRestoreRetry] = useState(null);
  // Bumped by the Retry button to re-run the restore effect - the
  // reloadNonce idiom Step3Verify established for its challenge load.
  const [restoreNonce, setRestoreNonce] = useState(0);

  // Tracks the most recent server-side sessionId we are aware of, even
  // if startIssuance later failed before step2Result was populated.
  // Used by handleReset to discard orphaned server sessions.
  const pendingSessionIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Bounded restore : see SHORT_CALL_TIMEOUT_MS and withTimeoutSignal
    // (apiClient.js). The captured controller also aborts on unmount /
    // StrictMode remount, and an AbortError is classified TRANSIENT
    // below (never the wipe path), so a development double-mount can
    // never destroy the saved pointer. The timer arms only once there
    // is a saved pointer to restore (2026-08-14 go-public review) :
    // the helper is invoked on the saved-pointer path only, so a
    // first-time visitor - who takes the no-saved-state early return -
    // never has a 15-second timer pending until unmount.
    let restoreController = null;
    (async () => {
      const saved = loadState();
      if (!saved) {
        if (!cancelled) {
          setRestored(true);
        }
        return;
      }
      try {
        // Look up the matching server for the saved serverId. If the
        // server allow-list has changed since the save we treat it as
        // a stale session and start over.
        if (saved.step1) {
          const matchingServer = ACME_SERVERS.find(
            (s) => s.id === saved.step1.serverId
          );
          if (!matchingServer) {
            // 2026-07-03 adversarial review finding : an unknown
            // serverId used to be SILENTLY skipped here while
            // step2Result / completedSteps / currentStep were still
            // restored below - a half-restore whose render then
            // dereferenced the null step1Result (Step 4's
            // `serverId={step1Result.serverId}` prop) and crash-
            // looped through the ErrorBoundary's Refresh, because
            // the crash happened in render where no catch could
            // clearState. Reachable whenever a saved serverId is not
            // in this build's allow-list : an ACME_SERVERS rename /
            // removal while sessions are live (possible since the
            // Valkey migration made sessions survive deploys), or a
            // session resumed from the server-side-only "local-test"
            // e2e entry. Throwing makes the catch below do what the
            // comment above always promised : wipe and start fresh.
            throw unrestorable("The saved ACME service is no longer offered.");
          }
          // loadedConfig (Configuration File body, including account
          // and SSL Certificate private keys + EAB HMAC) is no
          // longer persisted per the 2026-05-31 code review §1
          // fix - see src/client/issuance/state.js header. If the customer
          // was using Configuration File reuse, they re-drop the
          // file on this fresh page load. The server-held session
          // keyed by sessionId still has everything it needs to
          // continue an in-flight issuance.
          setStep1Result({
            serverId: matchingServer.id,
            serverUrl: matchingServer.url,
            label: matchingServer.label,
            loadedConfig: null,
          });
        }
        if (saved.step2 && saved.step2.sessionId) {
          // Verify the session is still alive on the server. Sessions
          // live in the Valkey store for 48 hours from CREATION (the
          // absolute cap sessionStore.js documents - re-reads never
          // extend it ; 2026-08-13 comment-accuracy fix : this used to
          // say "from their last write", the overstatement the store's
          // own header explicitly corrected). They survive server
          // restarts since the 2026-07-03 migration ; an expired or
          // discarded session 404s here and we fall through to a
          // fresh start.
          const view = await withTimeoutSignal(
            SHORT_CALL_TIMEOUT_MS,
            (signal, controller) => {
              restoreController = controller;
              return apiGetSession(saved.step2.sessionId, { signal });
            }
          );
          // 2026-07-03 PR #76 post-CI independent review (F4a) : the
          // apiClient `call` helper resolves an off-contract
          // 200-with-empty-body to `null` (see the `(json && json.data)
          // || null` return in src/client/issuance/apiClient.js:call). Reading
          // view.keyTypeId on a null view would throw a raw, UNTAGGED
          // TypeError, which the catch below treats as transient : the
          // saved pointer is kept and the customer is left on a Retry
          // screen that can never succeed while the server keeps
          // answering with an empty body. Throwing the tagged
          // unrestorable() BEFORE any property access gives the catch
          // the positive proof its wipe path requires, so the saved
          // pointer is cleared and the customer returns to Step 1. The
          // reason is deliberately just the cause : it reaches the
          // console for support only, and the customer sees the fixed
          // sentence the catch sets (see the setRestoreError comment
          // below).
          if (!view) {
            throw unrestorable("The server returned an empty session.");
          }
          const keyType = KEY_TYPES.find((k) => k.id === view.keyTypeId);
          // PR #76 review third pass : an unknown keyTypeId used to be
          // silently mapped to `undefined` here while currentStep /
          // completedSteps were still restored to Step 4 below - the
          // Step 4 render gate (step2Result.keyType being truthy) then
          // rendered NOTHING, stranding the customer on a dead step.
          // This is the SAME half-restore class the unknown-serverId
          // throw above handles ; throw so the catch below wipes state
          // and starts fresh. Reachable if a KEY_TYPES id is renamed /
          // removed while sessions are live (possible since the Valkey
          // migration made sessions survive deploys).
          if (!keyType) {
            throw unrestorable(
              "The saved SSL Certificate key type is no longer offered."
            );
          }
          // Track for Start Over cleanup, same as
          // handleStep2Complete (2026-08-14 go-public review,
          // "orphan-session-cleanup") : without this line, a reload
          // sitting between a resume-to-Step-2 and its resubmit left
          // pendingSessionIdRef null, so the documented
          // resubmit-orphan discard in handleStep2SessionCreated
          // silently skipped and the superseded EAB-bearing session
          // lived out its full 48-hour TTL. The ref must reflect
          // every server session the page is aware of, however it
          // became aware of it.
          pendingSessionIdRef.current = view.sessionId;
          setStep2Result({
            sessionId: view.sessionId,
            status: view.status,
            authorizations: view.authorizations || [],
            domains: view.domains,
            primaryDomain: view.primaryDomain,
            keyType,
          });
        }
        if (Array.isArray(saved.completedSteps)) {
          setCompletedSteps(saved.completedSteps);
        }
        if (typeof saved.currentStep === "number") {
          setCurrentStep(saved.currentStep);
        }
        if (!cancelled) {
          setRestored(true);
        }
      } catch (e) {
        // TRANSIENT-vs-GONE fork (2026-08-14 go-public review,
        // "state-machine-error-handling"). Only POSITIVE PROOF that
        // the saved session cannot be restored takes the wipe path :
        // the server's 404 (session expired / discarded - the
        // uniform not-found posture) or this effect's own TAGGED
        // schema throws (stale serverId / keyTypeId, empty view -
        // see unrestorable()). Everything else - the fetch never
        // reaching the server (status 0), the abort ceiling, a
        // per-IP 429, any 5xx including the store's deliberate
        // fail-closed 503, AND any unexpected runtime throw in the
        // mapping code above - keeps localStorage INTACT and renders
        // the Retry screen instead : wiping on anything less
        // than proof destroys the only automatic pointer to a
        // still-live 48-hour session for a customer who never saved
        // their Configuration File, mid-DNS-wait. (An unexpected throw
        // previously fell to the wipe side by default - the exact
        // outcome this fork exists to prevent.)
        const definitive =
          (e && e.isUnrestorable === true) ||
          (e instanceof ApiError && e.status === 404);
        if (!definitive) {
          if (!cancelled) {
            console.error(
              "SAVED SESSION RESTORE HIT A TRANSIENT FAILURE :",
              (e && e.message) || e
            );
            // Partial in-memory restore state is reset so the retry
            // maps everything from scratch ; the retry screen renders
            // instead of the steps while restored stays false, which
            // also keeps the persist effect below from overwriting
            // the saved snapshot with this empty state.
            setStep1Result(null);
            setStep2Result(null);
            setCompletedSteps([]);
            setCurrentStep(STEPS.SERVICE);
            setRestoreRetry(
              "Your saved issuance request could not be checked. Please try again in a moment."
            );
          }
          return;
        }
        // Definitively unrestorable - wipe and start fresh.
        clearState();
        setStep1Result(null);
        setStep2Result(null);
        setCompletedSteps([]);
        setCurrentStep(STEPS.SERVICE);
        if (!cancelled) {
          // 2026-08-13 go-public content ruling (audit :
          // "inaccurate-wording") : the old message pasted the
          // internal failure reason into customer text AND claimed "A
          // FRESH SESSION HAS BEEN STARTED" when nothing was started -
          // this block only clears local state and returns to Step 1.
          // The reason stays available for support via the console.
          //
          // The message must NOT assert a CAUSE. Only one of the four
          // branches that reach here (the server's 404) proves the
          // request is gone ; the three tagged unrestorable() throws -
          // an unknown serverId, an unknown keyTypeId, an empty view -
          // fire while the request is still alive on the server and
          // still reachable with the configuration file. Telling that customer it "has been completed or has
          // expired" and pointing them at starting over throws away a
          // live 48-hour request mid-DNS-wait. What IS true on every
          // branch is that this browser's saved pointer has just been
          // cleared, so that is what the customer is told.
          console.error("SAVED SESSION RESTORE FAILED :", e.message || e);
          setRestoreError(
            "Your saved issuance request could not be restored and has been cleared from this browser. If you downloaded a configuration file you can still open it below. Otherwise, please start a new issuance request."
          );
          setRestored(true);
        }
      }
    })();
    return () => {
      cancelled = true;
      // Abort an in-flight restore on unmount / StrictMode remount ;
      // withTimeoutSignal's own finally clears the timer. A completed
      // or never-started call makes this a no-op.
      if (restoreController) restoreController.abort();
    };
    // restoreNonce is the Retry re-run trigger (see its state
    // comment) ; the effect is otherwise mount-only.
  }, [restoreNonce]);

  // Persist state on every transition AFTER the initial restore has
  // committed. Gating on `restored` (a state value, not a ref) is what
  // makes this safe under React StrictMode : the restored values
  // and the `restored = true` flag land in the same commit, so the
  // save effect sees them together and never writes the pre-restore
  // values back over the saved snapshot.
  useEffect(() => {
    if (!restored) return;
    // step1Result.loadedConfig is held in memory for the current
    // page session but deliberately NOT forwarded to saveState -
    // the persistence layer strips secrets, but we also do not
    // pass them to it. See src/client/issuance/state.js header for the
    // rationale (2026-05-31 code review §1).
    saveState({
      currentStep,
      completedSteps,
      step1: step1Result ? { serverId: step1Result.serverId } : null,
      step2: step2Result ? { sessionId: step2Result.sessionId } : null,
    });
  }, [restored, currentStep, completedSteps, step1Result, step2Result]);

  const stepStatus = (n) => {
    if (completedSteps.includes(n)) return "completed";
    if (currentStep === n) return "active";
    return "disabled";
  };

  const handleStep1Complete = (result) => {
    // A successful forward action retires the restore-failure warning
    // (2026-08-14 go-public review, "contradictory-ui-state") : left
    // up, the page would keep asking the customer to start a new
    // issuance request above a live later step - contradictory
    // instructions inviting them to abandon a healthy order.
    setRestoreError(null);
    setStep1Result(result);
    setCompletedSteps((prev) =>
      prev.includes(STEPS.SERVICE) ? prev : [...prev, STEPS.SERVICE]
    );
    setCurrentStep(STEPS.CONFIGURE);
  };

  const handleStep2SessionCreated = (sessionId) => {
    // 2026-05-31 seventh-round code review I6 : if a previous
    // Step 2 attempt already minted a server-side session and the
    // customer comes back to edit-and-resubmit, that previous
    // sessionId is about to be lost. Without a DELETE the orphan
    // session lives out its 48-hour Time To Live (TTL) as an
    // encrypted blob in the Valkey store, keeping the customer's
    // keys + External Account Binding (EAB) credentials retrievable
    // by anyone holding the orphan's cookie. Fire a
    // best-effort DELETE BEFORE we overwrite the ref. Async ; we
    // do not await because the new session create is already
    // mid-flight and we do not want to block the customer's
    // happy path on the old discard. The DELETE route is
    // idempotent + does not need the cookie to succeed for an
    // already-gone session, so a lost race is harmless.
    const oldSessionId = pendingSessionIdRef.current;
    if (oldSessionId && oldSessionId !== sessionId) {
      // Fire-and-forget : we deliberately do not await. The
      // catch swallow is the standard discard-failure posture ;
      // an unreachable server during retry is not worth
      // surfacing to the customer in the middle of a
      // successful retry.
      apiDiscardSession(oldSessionId).catch(() => {
        // ignore - discard is best-effort cleanup of an
        // orphan ; TTL sweep is the safety net.
      });
    }
    pendingSessionIdRef.current = sessionId;
  };

  const handleStep2Complete = (result) => {
    pendingSessionIdRef.current = result.sessionId;
    setStep2Result(result);
    setCompletedSteps((prev) =>
      prev.includes(STEPS.CONFIGURE) ? prev : [...prev, STEPS.CONFIGURE]
    );
    // 2026-05-31 sixth-round code review #4 : clear the in-memory
    // Configuration File payload now that Step 2 has shipped it to
    // the server. step1Result.loadedConfig holds the account
    // Privacy-Enhanced Mail (PEM) string, the SSL Certificate PEM,
    // and the External Account Binding (EAB) Hash-based Message
    // Authentication Code (HMAC) key. Once Step 2 has run, the
    // server is the canonical owner of that material ; keeping a
    // browser-side copy in React state for the rest of the page
    // lifetime is dead weight that an XSS payload could exfiltrate.
    // Mirrors the eabKid / eabHmac clear inside Step2Configure
    // (the wipe handleSubmit runs after a successful start) - the
    // pattern : once the data has been handed to the server, drop
    // it from browser state.
    if (step1Result && step1Result.loadedConfig) {
      setStep1Result({ ...step1Result, loadedConfig: null });
    }
    // 2026-05-31 sixth-round code review #8 : resume against an
    // already-issued order. The server returns status === "issued"
    // with NO pending authorizations (the order is past finalize)
    // so the previous `allValid && length > 0` gate stayed false
    // and dropped the customer onto Step 3, which then rendered an
    // empty "nothing to verify" state. Route the issued case
    // directly to Step 4 so the customer's next click downloads
    // the SSL Certificate.
    //
    // 2026-07-03 PR #76 sixth-pass review (Finding 4c) : a
    // "validated" result routes to Step 4 as well. A "validated"
    // order is past domain validation but not yet finalized ; Step 4's
    // entry call runs finalize from there. In practice the allValid
    // branch below already catches this (a validated order has every
    // authorization "valid"), so the status check is the robust half :
    // it holds even when the authorization list comes back empty.
    // This handler is the only place the page routes by the session's
    // status - the localStorage restore in the mount effect reopens
    // the saved currentStep as it stands.
    if (result.status === "issued" || result.status === "validated") {
      setCompletedSteps((prev) => {
        const next = prev.slice();
        if (!next.includes(STEPS.VERIFY)) next.push(STEPS.VERIFY);
        return next;
      });
      setCurrentStep(STEPS.DOWNLOAD);
      return;
    }
    // PR #76 review third pass : guard result.authorizations with
    // `|| []`, matching the defensive pattern every sibling path already
    // uses (the restore effect does the same `|| []` guard).
    // buildPublicView always emits an authorizations array today, so
    // this is defensive-consistency only : a missing array must not
    // throw on the .length / .every access here.
    const auths = result.authorizations || [];
    const allValid =
      auths.length > 0 && auths.every((a) => a.status === "valid");
    if (allValid) {
      setCompletedSteps((prev) =>
        prev.includes(STEPS.VERIFY) ? prev : [...prev, STEPS.VERIFY]
      );
      setCurrentStep(STEPS.DOWNLOAD);
    } else {
      setCurrentStep(STEPS.VERIFY);
    }
  };

  const handleStep3Complete = () => {
    setCompletedSteps((prev) =>
      prev.includes(STEPS.VERIFY) ? prev : [...prev, STEPS.VERIFY]
    );
    setCurrentStep(STEPS.DOWNLOAD);
  };

  const handleStep4Success = () => {
    // Mark step 4 completed. (This used to feed the StepIndicator
    // checkmark ; the operator directive 2026-07-05 removed that strip
    // entirely, but the completedSteps entry is still load-bearing :
    // it is persisted so a reload restores the customer onto Step 4,
    // and stepStatus(4) === "completed" keeps Step4Download mounted
    // through the terminal StepPanel.) This never collapses the
    // panel : the Step 4 render branch keeps Step4Download mounted for
    // both "active" and "completed", so the issued artifacts stay on
    // screen (PR #76 review blocking-bug fix, 2026-07-03). The bundle
    // itself lives in Step4Download's own state, so the page keeps no
    // copy.
    setCompletedSteps((prev) =>
      prev.includes(STEPS.DOWNLOAD) ? prev : [...prev, STEPS.DOWNLOAD]
    );
    // Intentionally do NOT clear localStorage here - the customer
    // may reload the page after issuance and expect to land back on
    // Step 4 with the same sessionId so they can re-download the
    // bundle or the Configuration File. The server-held session
    // is bounded by the 48-hour Time To Live (TTL) sweep ; once
    // it expires apiGetSession returns 404 on restore and the
    // page wipes localStorage and starts fresh. The 2026-06-02
    // fourth-round code review #2 caught an earlier comment that
    // claimed this code cleared localStorage - removed.
  };

  const handleReset = () => {
    // Best-effort cleanup of any server-side session we know about,
    // including sessions that were created but never made it through
    // startIssuance (pendingSessionIdRef captures those).
    //
    // 2026-07-04 CI review 4880340879 finding #6 : the LOCAL reset runs
    // FIRST and the discard is fire-and-forget. The previous shape
    // awaited apiDiscardSession before touching any local state, with
    // no timeout on the fetch - a hung connection stranded the customer
    // on a dead Start Over click, staring at stale state. The discard
    // is best-effort by design (handleStep2SessionCreated already
    // fire-and-forgets the same call ; the 48-hour TTL sweep is the
    // safety net), so nothing about it may gate the customer's reset.
    const sessionIdToDiscard =
      step2Result?.sessionId || pendingSessionIdRef.current;
    if (sessionIdToDiscard) {
      apiDiscardSession(sessionIdToDiscard).catch((e) => {
        // 2026-05-31 seventh-round code review nit-2 : console.warn
        // gives operators a breadcrumb if a customer reports a
        // failed cleanup without surfacing the error to the UI -
        // Start Over always completes locally (the session expires
        // after 48 hours anyway). warn, not error, to keep the
        // operator signal/noise ratio clean in normal use.
        console.warn(
          "Best-effort session discard failed (TTL sweep will collect)",
          e && e.message
        );
      });
    }
    pendingSessionIdRef.current = null;
    setStep1Result(null);
    setStep2Result(null);
    setCompletedSteps([]);
    setCurrentStep(STEPS.SERVICE);
    setRestoreError(null);
    // 2026-07-04 CI review 4881104837 finding #5 (reviewed, working as
    // intended - do not "fix") : clearState() wipes the saved snapshot,
    // and the restored-gated persist effect above (the `if (!restored)
    // return` useEffect) then re-writes a FRESH EMPTY payload once the
    // state resets in this handler commit. That is deliberate : a
    // fresh empty payload restores to the identical fresh start as no
    // payload at all, so the re-write is harmless, and wiping here
    // keeps Start Over correct even if the page unloads before the
    // persist effect runs.
    clearState();
  };

  /*
    ONE header for every state this page can render.

    Defined once and reused by the restore-in-progress screen, the
    restore-retry screen and the main flow, because the operator's
    2026-08-15 directive is that the description never disappears -
    and the two restore screens previously rendered an Alert or a
    grey sentence alone in an empty viewport, with no h1, no
    description and no Security Notice. Every sibling tool keeps this
    block on screen in every state.

    Shape is the site canon : an mb-8 block holding the h1 with mb-4
    and an introductory PARAGRAPH in base-size prose (the canon since
    the operator's 2026-08-28 ruling, docs/UI_STANDARD.md page
    skeleton rule 1), then the Security Notice with className="mb-6"
    (compare the top of src/client/pages/PFXConverter.jsx and every
    other tool page).
  */
  const pageHeader = (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          Hosted SSL Certificate as a Service (CaaS) ACME Issuance Tool
        </h1>
        <p className="text-body">
          Issue an SSL Certificate using the External Account Binding (EAB)
          credentials from your Trustico® Certificate as a Service (CaaS)
          license. You prove control of each domain name with a Domain Name
          System (DNS) record or an uploaded file, and the Trustico® server
          handles the ACME conversation with the Certificate Authority (CA). No
          client software is needed, and the issued SSL Certificate and its
          files download straight from this page.
        </p>
      </div>

      <SecurityNotice />
    </>
  );

  if (!restored) {
    if (restoreRetry) {
      // Transient restore failure : the saved pointer is intact and
      // the steps stay hidden behind this screen until a retry
      // succeeds (or definitively 404s into the wipe path). Retry
      // is the primary action - but NOT the only one
      // (2026-08-14 go-public review) : a PERSISTENT transient-classified
      // failure (the session store fail-closed while the rest of the
      // site works, a long 5xx) previously wedged the customer here
      // with no way to even abandon the dead order, so the explicit
      // start-fresh escape discards the saved pointer and opens
      // Step 1. Discarding is the CUSTOMER's deliberate choice on a
      // labelled control - the opposite of the silent automatic wipe
      // the transient fork exists to prevent.
      return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {pageHeader}
          <Alert
            type="warning"
            title="Issuance Request Restore Failed"
            className="mb-6"
            live
          >
            <p className="mb-3">{restoreRetry}</p>
            {/* Prose rules (operator directive 2026-08-15) : sentence
                joins are " - " and never " : ", and the configuration
                file is a descriptive noun in a sentence, so it is
                lowercase here while the control that produces it keeps
                its Title Case label. */}
            <p className="mb-4">
              Your saved issuance request has not been removed. This was a
              temporary problem reaching the Trustico® server, and trying again
              in a moment will usually restore it exactly where you left off. If
              the problem continues you can start a new issuance request instead
              - that clears what this browser has saved, and the issuance
              request itself stays reachable with your configuration file for
              the rest of its {SESSION_LIFETIME_HOURS} hours.
            </p>
            <div className="flex justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                <ActionButton
                  variant="primary"
                  icon={ArrowPathIcon}
                  onClick={() => {
                    setRestoreRetry(null);
                    setRestoreNonce((n) => n + 1);
                  }}
                >
                  Retry
                </ActionButton>
                <ActionButton
                  variant="outline"
                  onClick={() => {
                    // Best-effort server discard BEFORE the local
                    // clear (2026-08-14 go-public review) : the saved
                    // pointer is still readable here, and abandoning
                    // it without the discard left the session's
                    // encrypted blob - ACME account key, SSL
                    // Certificate Private Key, EAB credentials -
                    // live for its full 48 hours. Fire-and-forget,
                    // exactly like handleReset : nothing about the
                    // cleanup may gate the customer's fresh start
                    // (the TTL sweep is the safety net).
                    const saved = loadState();
                    const abandonedId = saved?.step2?.sessionId;
                    if (abandonedId) {
                      apiDiscardSession(abandonedId).catch((e) => {
                        console.warn(
                          "Best-effort session discard failed (TTL sweep will collect)",
                          e && e.message
                        );
                      });
                    }
                    clearState();
                    setRestoreRetry(null);
                    setRestored(true);
                  }}
                >
                  Start Another Issuance Request
                </ActionButton>
              </div>
            </div>
          </Alert>
        </div>
      );
    }
    return (
      // 2026-05-31 seventh-round code review I10 : role="status"
      // plus aria-live="polite" so screen-reader users get an
      // announcement during the brief restore window. Without it
      // the page renders empty until restored flips true and
      // assistive technology gives no cue that work is in
      // progress.
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {pageHeader}
        {/* Left-aligned base-size prose, like every other sentence on
            the site : this line was centered and text-sm, the only
            centered body text on the page. */}
        <p className="text-body" role="status" aria-live="polite">
          Restoring your saved issuance request...
        </p>
        <p className="text-body mt-3">
          We are checking whether the issuance request you started earlier is
          still open. This takes a moment.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="Hosted SSL Certificate as a Service (CaaS) ACME Issuance Tool"
        description="Issue an SSL Certificate using the credentials from your Trustico® Certificate as a Service (CaaS) license. Prove you control your domain names by DNS record or by uploading a file, then download your issued SSL Certificate and its files. Supports single, multi-domain and Wildcard SSL Certificates."
        keywords="SSL Certificate issuance, issue SSL Certificate online, hosted SSL Certificate issuance, ACME SSL Certificate, External Account Binding, EAB credentials, DNS-01 validation, HTTP-01 validation, Wildcard SSL Certificate issuance, SSL Certificate download, Trustico®"
        canonicalUrl="https://tools.trustico.com/issuance"
      />

      {/*
        The same header every other state of this page renders - see
        the pageHeader definition above for why it is shared and why
        the sentences that used to pad this block moved elsewhere (the
        server-side ACME explanation into Step 1's own card
        description, and the no-automatic-renewal consequence, operator
        content ruling 2026-08-13, into Step 4's "Next Steps"
        block).
      */}
      {pageHeader}

      {restoreError && (
        <Alert
          type="warning"
          title="Saved Issuance Request Cleared from This Browser"
          className="mb-6"
          live
          onClose={() => setRestoreError(null)}
        >
          {restoreError}
        </Alert>
      )}

      {/*
        The standalone "Before You Begin" card is GONE (operator
        directive 2026-08-15 : "your before you begin box looks
        crap"). No sibling tool page carries a free-standing
        information card between the Security Notice and its first
        working section, so this page no longer does either.

        Its content is NOT lost - the prerequisites it listed
        (operator content ruling 2026-08-13) are now the second
        paragraph of Step 1's own card description, which is where
        every sibling tool puts explanatory prose : inside the card
        the customer is about to act in, not stacked above it.
      */}

      {/*
        Operator directive 2026-07-05 ("The Step1, Step2, Step3, Step4
        at the top just looks too crowded and i wonder if it is even
        needed as each section has step 1, step 2 etc anyway") : the
        StepIndicator strip is REMOVED - each section's own
        "Step N : Title" heading already carries the position, and the
        completed sections collapse to their compact check-marked
        summary rows, so the page reads as its own progress display.

        What survives the removal is the 2026-05-31 seventh-round code
        review I10 accessibility affordance : the indicator's wrapping
        div carried role="status" + aria-live so assistive technology
        announced every step transition. This sr-only live region keeps
        that exact announcement ("Step N : Title", re-rendered whenever
        currentStep changes) with zero visual footprint. Polite (not
        assertive) because step transitions are progress updates, not
        urgent interrupts - unchanged from the I10 posture.
      */}
      <div role="status" aria-live="polite" className="sr-only">
        {/*
          Guarded : the restore effect accepts any numeric
          saved.currentStep, so a corrupted persisted value outside
          1 to 4 would otherwise announce "Step 7 : undefined". An
          unknown step announces nothing rather than nonsense.
        */}
        {STEP_TITLES[currentStep]
          ? `Step ${currentStep} : ${STEP_TITLES[currentStep]}`
          : null}
      </div>

      {/* Step 1 - Select Your Service */}
      {/*
        Operator directive 2026-07-05 ("make step one this huge long
        blue box... it is ugly in this format") : the completed
        rendering no longer passes a bg-blue-50 detail box as children.
        StepPanel collapses a completed step to its compact neutral
        summary row, and the `summary` prop carries the customer's
        selection ("<service label> Selected"). The serverUrl detail
        line is dropped from the collapsed view on purpose : the label
        identifies the choice, and the URL is developer-grade noise in
        a one-line summary.
      */}
      <StepPanel
        number={1}
        title="Select Your Service"
        // Two paragraphs (see the description JSDoc in StepPanel.jsx) :
        // the service choice, then the prerequisites that used to sit
        // in the free-standing "Before You Begin" card above the page.
        // The sentence join is " - " and not " : " per the tool-UI
        // prose rule (operator ruling 2026-08-15).
        description={
          <>
            <p>
              Choose the Automatic Certificate Management Environment (ACME)
              service that matches the SSL Certificate you purchased. Your
              External Account Binding (EAB) credentials work with one service
              only.
            </p>
            <p>
              Before you start you will need an active Trustico® Certificate as
              a Service (CaaS) license, every domain name the SSL Certificate
              will cover, and access to your Domain Name System (DNS) records or
              the ability to upload a file to your web server. Your key
              identifier and HMAC key are sent to you by e-mail and are also
              shown on the order details page in the Trustico® ordering and
              billing system.
            </p>
          </>
        }
        status={stepStatus(1)}
        summary={step1Result ? `${step1Result.label} Selected` : null}
      >
        {stepStatus(1) === "active" && (
          <Step1Service
            onComplete={handleStep1Complete}
            onReset={handleReset}
          />
        )}
      </StepPanel>

      {/* Step 2 - Configure SSL Certificate */}
      {/*
        Summary (operator directive 2026-07-05, compact completed
        rows) : the two facts the old blue box carried, on one line in
        the house " ; " list separator and space-colon label style. The
        keyType guard mirrors the old completed-children gate - a
        half-restored step2Result without a mapped keyType renders the
        bare check row rather than "Key Type : undefined".
      */}
      <StepPanel
        number={2}
        title="Configure SSL Certificate"
        description="Enter the domains for the SSL Certificate, choose your key type, and provide your license credentials."
        status={stepStatus(2)}
        // The ACME acronym is expanded once per screen, in Step 1's
        // description directly above ; repeating the full 52-character
        // expansion here made a first-time customer read it twice
        // inside one viewport.
        disabledMessage="This step unlocks once an ACME service has been selected."
        // Separator is " - " per the tool-UI prose ruling
        // (2026-08-15). The middle dot this used to carry was the only
        // occurrence of that character in the whole client tree.
        //
        // domains is guarded for length, not just presence : a session
        // view carrying an empty array would otherwise render the
        // literal "undefined" into the completed summary row.
        summary={
          step2Result && step2Result.keyType && step2Result.domains?.length
            ? `${step2Result.domains[0]}${
                step2Result.domains.length > 1
                  ? ` + ${step2Result.domains.length - 1} more`
                  : ""
              } - ${step2Result.keyType.label.replace(" (Recommended)", "")}`
            : null
        }
      >
        {stepStatus(2) === "active" && step1Result && (
          <Step2Configure
            serverId={step1Result.serverId}
            loadedConfig={step1Result.loadedConfig || null}
            onComplete={handleStep2Complete}
            onSessionCreated={handleStep2SessionCreated}
            onReset={handleReset}
          />
        )}
      </StepPanel>

      {/* Step 3 - Verify Domain Ownership */}
      {/*
        Summary (operator directive 2026-07-05, compact completed
        rows) : the same Title-Case sentence the old completed body
        rendered, now inside the compact row (period dropped : the row
        is a status line, not prose).
      */}
      <StepPanel
        number={3}
        title="Verify Domain Ownership"
        description="Before your SSL Certificate can be issued, the Certificate Authority (CA) must confirm that you control every domain it will cover. For each domain you will add a record or a file somewhere you control, then ask for it to be checked. Checks are usually quick but can take several minutes, and you can leave and come back."
        status={stepStatus(3)}
        disabledMessage="This step unlocks once the issuance request has been created."
        summary="Domains Authorized"
      >
        {stepStatus(3) === "active" && step2Result && (
          // No authorizations prop : the step derives everything it
          // renders from its own /challenges response, so the two can
          // never disagree (see the `details.map` that draws one card
          // per domain, and the `pendingDetails` the Continue gate
          // reads, in Step3Verify.jsx). Passing this page-state
          // snapshot let a domain that had become valid since Step 2
          // completed fall between the two lists and vanish from the
          // step.
          <Step3Verify
            sessionId={step2Result.sessionId}
            primaryDomain={step2Result.primaryDomain}
            onComplete={handleStep3Complete}
            onReset={handleReset}
          />
        )}
      </StepPanel>

      {/* Step 4 - Download SSL Certificate */}
      <StepPanel
        number={4}
        title="Download SSL Certificate"
        description="Download your issued SSL Certificate together with its intermediate SSL Certificates, the SSL Certificate with chain, and your private key. Your server may ask for the SSL Certificate with chain and the private key, or for the SSL Certificate and its intermediates as separate files, so every form is provided here."
        status={stepStatus(4)}
        disabledMessage="This step unlocks once every domain on the SSL Certificate has been validated."
        // terminal : Step 4 must NEVER collapse to the compact
        // completed summary row - collapsing would unmount the issued
        // SSL Certificate and Private Key, the exact PR #76 review
        // blocking bug the render branch below exists to prevent. See
        // the `terminal` JSDoc in StepPanel.jsx.
        terminal
      >
        {/*
          Step 4 renders Step4Download while the step is ACTIVE or
          COMPLETED, from ONE branch, so the component stays mounted
          across the active→completed transition. This is the fix for
          the PR #76 review blocking bug (2026-07-03) : issuance
          success calls onSuccess automatically (not on a click), which
          marks step 4 completed ; the previous gate rendered
          Step4Download only while "active", so the instant the SSL
          Certificate + Private Key arrived the component unmounted and
          was replaced by a static one-liner - the customer could never
          copy or download the key that the whole tool exists to
          deliver, and a reload re-collapsed it. Step 4 is TERMINAL, so
          "completed" must keep its artifacts on screen, not collapse
          to a summary the way steps 1 to 3 do. Step4Download owns its
          own working / processing / complete / failed rendering ;
          keeping it mounted preserves that state (and on a fresh
          reload it re-runs its idempotent finalize check and shows the
          bundle again). step1Result is part of the gate because the
          props below dereference it (2026-07-03 adversarial review
          half-restore finding : the render-side belt-and-braces).
        */}
        {(stepStatus(4) === "active" || stepStatus(4) === "completed") &&
          step1Result &&
          step2Result &&
          step2Result.keyType && (
            <Step4Download
              sessionId={step2Result.sessionId}
              primaryDomain={step2Result.primaryDomain}
              domains={step2Result.domains}
              certKeyTypeId={step2Result.keyType.id}
              serverId={step1Result.serverId}
              serverUrl={step1Result.serverUrl}
              onSuccess={handleStep4Success}
              onReset={handleReset}
            />
          )}
      </StepPanel>

      {/*
        Operator directive 2026-07-05 ("you have instead thrown this at
        the bottom of the page where even I can't see it") : the
        page-bottom Start Over button is REMOVED. Every step component
        now renders Start Over inside its own centered action row
        (wired to the SAME handleReset above - the handler and its
        local-reset-first + fire-and-forget-discard behaviour are
        unchanged ; only the trigger placement moved).
      */}
    </div>
  );
}

export default Issuance;
